import { useState } from "react";
import { createPortal } from "react-dom";
import {
  IoAlertCircleOutline,
  IoCheckmark,
  IoChevronBack,
  IoTimeOutline,
} from "react-icons/io5";
import { toast } from "react-toastify";
import useTranslator from "../../hooks/useTranslator";
import axiosInstance from "../../services/authWrapper";
import ClientIdentificationForm from "../ClientIdentificationForm/ClientIdentificationForm";
import { Icon } from "../design";
import {
  TENANT_REQUIREMENTS,
  getHouseholdSummaryParts,
} from "../../helper/common";
import { resolveParticipantName } from "../../helper/applicationHelpers";
import constants from "../../Utils/constants";
import "./BeforeSharingModal.css";

const PET_TYPES = [
  { value: "dog", key: "householdPetDog" },
  { value: "cat", key: "householdPetCat" },
  { value: "other", key: "householdPetOther" },
];

const isSigned = (status) => status === constants.ACCEPTED;

// "Residents: 3 (2 adults, 1 minor) · Pets: Dog (PPP: No)"
const householdSummary = (data, _t) => {
  const parts = getHouseholdSummaryParts(data, _t);
  if (!parts) return "";
  // Parts not asked are empty
  const people = [parts.residents, parts.breakdown].filter(Boolean).join(" ");
  const pets = [parts.pets, parts.ppp].filter(Boolean).join(" ");
  return [people, pets].filter(Boolean).join(" · ");
};

const ChoicePill = ({ selected, onClick, children }) => (
  <button
    type="button"
    className={`bsm-pill${selected ? " is-selected" : ""}`}
    aria-pressed={selected}
    onClick={onClick}
  >
    {children}
  </button>
);

const Counter = ({ label, value, min, invalid, onChange }) => (
  <div className="bsm-counter-field">
    <div className="bsm-counter-label">{label}</div>
    <div className={`bsm-counter${invalid ? " is-invalid" : ""}`}>
      <button
        type="button"
        aria-label={`${label} -`}
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
      >
        −
      </button>
      <span>{value}</span>
      <button
        type="button"
        aria-label={`${label} +`}
        onClick={() => onChange(value + 1)}
      >
        +
      </button>
    </div>
  </div>
);

const Header = ({ eyebrow, title, subtitle, onClose, closable = true, _t }) => (
  <div className="bsm-header">
    <div>
      <div className="bsm-eyebrow">{eyebrow}</div>
      <h2 className="bsm-title">{title}</h2>
      {subtitle && <p className="bsm-subtitle">{subtitle}</p>}
    </div>
    {closable && (
      <button
        type="button"
        className="bsm-close"
        aria-label={_t("close")}
        onClick={onClose}
      >
        <Icon name="close" size={18} />
      </button>
    )}
  </div>
);

const StatusIcon = ({ state }) => (
  <span className={`bsm-status is-${state}`}>
    {state === "done" && <IoCheckmark />}
    {state === "open" && <span className="bsm-status-dot" />}
    {state === "waiting" && <IoTimeOutline />}
  </span>
);

const HouseholdPetsStep = ({
  eyebrow,
  applicationId,
  applicationTitle,
  landlordName,
  minAdults,
  // Which questions to ask (requirements_config applicant_questions):
  // household_members = adults, minors = minors, dogs = the whole pets part.
  // null: all asked, as before the config existed.
  questions,
  initialData,
  sharesOnComplete,
  onBack,
  onClose,
  closable,
  onCompleted,
  _t,
}) => {
  const askAdults = questions?.household_members !== false;
  const askMinors = questions?.minors !== false;
  const askPets = questions?.dogs !== false;

  const [adults, setAdults] = useState(initialData?.adults ?? minAdults);
  const [minors, setMinors] = useState(initialData?.minors ?? 0);
  const [hasPets, setHasPets] = useState(initialData?.has_pets ?? null);
  const [petTypes, setPetTypes] = useState(initialData?.pet_types ?? []);
  const [dangerousDog, setDangerousDog] = useState(
    initialData?.dangerous_dog ?? null
  );
  const [saving, setSaving] = useState(false);

  const adultsTooLow = askAdults && adults < minAdults;
  const hasDog = askPets && hasPets === true && petTypes.includes("dog");
  const canComplete =
    !adultsTooLow &&
    (!askPets ||
      (hasPets !== null &&
        (hasPets === false || petTypes.length > 0) &&
        (!hasDog || dangerousDog !== null)));

  const togglePetType = (value) =>
    setPetTypes((current) =>
      current.includes(value)
        ? current.filter((type) => type !== value)
        : [...current, value]
    );

  const handleComplete = async () => {
    if (!canComplete || saving) return;
    setSaving(true);
    try {
      // A part not asked now keeps its earlier answer (another landlord may
      // have asked it); never answered: not sent
      const pets = askPets
        ? {
            has_pets: hasPets,
            pet_types: hasPets ? petTypes : [],
            dangerous_dog: hasDog ? dangerousDog : null,
          }
        : {
            has_pets: initialData?.has_pets ?? null,
            pet_types: initialData?.pet_types ?? [],
            dangerous_dog: initialData?.dangerous_dog ?? null,
          };
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_SERVER_URL}/tenants/household-pets`,
        {
          application_id: applicationId ?? undefined,
          household_data: {
            // An earlier count below today's tenants cannot be fixed here
            adults: askAdults
              ? adults
              : (initialData?.adults ?? 0) >= minAdults
                ? initialData.adults
                : null,
            minors: askMinors ? minors : (initialData?.minors ?? null),
            ...pets,
          },
        }
      );
      await onCompleted(response?.data?.success_res?.household_pets ?? null);
    } catch (error) {
      toast.error(error?.response?.data?.message || _t("householdSaveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Header
        eyebrow={eyebrow}
        title={_t("householdAndPets")}
        subtitle={
          applicationId
            ? `${_t("householdAnsweredOnceFor")} ${applicationTitle ?? ""}. ${_t("householdAnyMemberCanEdit")}`
            : _t("householdAnsweredOnceForProfile")
        }
        onClose={onClose}
        closable={closable}
        _t={_t}
      />
      <div className="bsm-body">
        <button type="button" className="bsm-back" onClick={onBack}>
          <IoChevronBack />
          {_t("backToSteps")}
        </button>

        {(askAdults || askMinors) && (
          <>
            <div className="bsm-question">
              {askAdults && askMinors
                ? _t("householdHowManyAdultsMinors")
                : askAdults
                  ? _t(
                      "householdHowManyAdults",
                      "How many adults will live there?"
                    )
                  : _t(
                      "householdHowManyMinors",
                      "How many minors will live there?"
                    )}
            </div>
            <div className="bsm-counters">
              {askAdults && (
                <Counter
                  label={_t("adults")}
                  value={adults}
                  min={1}
                  invalid={adultsTooLow}
                  onChange={setAdults}
                />
              )}
              {askMinors && (
                <Counter
                  label={_t("minors")}
                  value={minors}
                  min={0}
                  onChange={setMinors}
                />
              )}
            </div>
            {adultsTooLow && (
              <div className="bsm-error" role="alert">
                <IoAlertCircleOutline />
                <span>
                  {_t("householdAdultsLowerThanTenants")} ({minAdults}).
                </span>
              </div>
            )}
            {askAdults && askMinors && (
              <div className="bsm-total">
                {_t("totalResidents")}: <strong>{adults + minors}</strong>
              </div>
            )}
          </>
        )}

        {askPets && (
          <>
            <div className="bsm-question">
              {_t("householdWillYouLiveWithPets")}
            </div>
            <div className="bsm-pills">
              <ChoicePill
                selected={hasPets === true}
                onClick={() => setHasPets(true)}
              >
                {_t("yes")}
              </ChoicePill>
              <ChoicePill
                selected={hasPets === false}
                onClick={() => setHasPets(false)}
              >
                {_t("no")}
              </ChoicePill>
            </div>
          </>
        )}

        {askPets && hasPets === true && (
          <>
            <div className="bsm-question">{_t("householdWhatTypeOfPet")}</div>
            <div className="bsm-pills">
              {PET_TYPES.map((pet) => (
                <ChoicePill
                  key={pet.value}
                  selected={petTypes.includes(pet.value)}
                  onClick={() => togglePetType(pet.value)}
                >
                  {_t(pet.key)}
                </ChoicePill>
              ))}
            </div>
          </>
        )}

        {hasDog && (
          <>
            <div className="bsm-question">{_t("householdIsDangerousDog")}</div>
            <div className="bsm-pills">
              <ChoicePill
                selected={dangerousDog === true}
                onClick={() => setDangerousDog(true)}
              >
                {_t("yes")}
              </ChoicePill>
              <ChoicePill
                selected={dangerousDog === false}
                onClick={() => setDangerousDog(false)}
              >
                {_t("no")}
              </ChoicePill>
            </div>
          </>
        )}
      </div>
      <div className="bsm-footer bsm-footer-actions">
        <span>
          {sharesOnComplete &&
            `${_t("completingThisStepSharesWith")} ${landlordName}.`}
        </span>
        <button
          type="button"
          className="bsm-primary"
          disabled={!canComplete || saving}
          onClick={handleComplete}
        >
          {saving ? _t("saving") : _t("complete")}
        </button>
      </div>
    </>
  );
};

// Key of the applicant questions step (LinkedIn and the workspace's own
// questions). Always optional: it is never required before sharing.
export const APPLICANT_QUESTIONS_STEP = "applicant_questions";

// "Teacher", "Yes", "2, 3", "2026-01-31" for the step description
const answerText = (answer, _t) => {
  if (answer === true) return _t("yes");
  if (answer === false) return _t("no");
  if (Array.isArray(answer)) return answer.join(", ");
  return String(answer);
};

const ApplicantQuestionInput = ({ question, value, onChange, _t }) => {
  if (question.type === "yes_no") {
    return (
      <div className="bsm-pills">
        {[true, false].map((option) => (
          <ChoicePill
            key={String(option)}
            selected={value === option}
            onClick={() => onChange(value === option ? null : option)}
          >
            {option ? _t("yes") : _t("no")}
          </ChoicePill>
        ))}
      </div>
    );
  }
  if (question.type === "single_choice") {
    return (
      <div className="bsm-pills">
        {question.options.map((option) => (
          <ChoicePill
            key={option}
            selected={value === option}
            onClick={() => onChange(value === option ? null : option)}
          >
            {option}
          </ChoicePill>
        ))}
      </div>
    );
  }
  if (question.type === "multiple_choice") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="bsm-pills">
        {question.options.map((option) => (
          <ChoicePill
            key={option}
            selected={selected.includes(option)}
            onClick={() =>
              onChange(
                selected.includes(option)
                  ? selected.filter((item) => item !== option)
                  : [...selected, option]
              )
            }
          >
            {option}
          </ChoicePill>
        ))}
      </div>
    );
  }
  const inputType =
    question.type === "number"
      ? "number"
      : question.type === "date"
        ? "date"
        : question.type === "link"
          ? "url"
          : "text";
  return (
    <input
      className="bsm-input"
      type={inputType}
      maxLength={question.type === "text" ? 1000 : undefined}
      value={value ?? ""}
      aria-label={question.text}
      onChange={(event) =>
        onChange(
          question.type === "number" && event.target.value !== ""
            ? Number(event.target.value)
            : event.target.value
        )
      }
    />
  );
};

// LinkedIn and the workspace's own questions. Every answer is optional.
// `data`: { workspace_id, questions: { linkedin, custom }, saved }
const ApplicantQuestionsStep = ({
  eyebrow,
  landlordName,
  data,
  sharesOnComplete,
  onBack,
  onClose,
  closable,
  onCompleted,
  _t,
}) => {
  const { questions, saved } = data;
  const [linkedin, setLinkedin] = useState(saved?.linkedin ?? "");
  const [answers, setAnswers] = useState(() =>
    Object.fromEntries(
      (saved?.answers ?? []).map((answer) => [answer.question_id, answer.value])
    )
  );
  const [saving, setSaving] = useState(false);

  // Optional by default. When the workspace requires it, every question
  // asked (LinkedIn included) needs an answer. "No" and 0 are answers.
  const isAnswered = (value) =>
    !(
      value === undefined ||
      value === null ||
      (typeof value === "string" && value.trim() === "") ||
      (Array.isArray(value) && value.length === 0)
    );
  const canComplete =
    !questions.required ||
    ((!questions.linkedin || isAnswered(linkedin)) &&
      questions.custom.every((question) => isAnswered(answers[question.id])));

  const handleComplete = async () => {
    if (saving || !canComplete) return;
    setSaving(true);
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_SERVER_URL}/tenants/applicant-questions`,
        { workspace_id: data.workspace_id, linkedin, answers }
      );
      await onCompleted(response?.data?.success_res?.saved ?? null);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || _t("applicantQuestionsSaveError")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Header
        eyebrow={eyebrow}
        title={_t("applicantQuestions")}
        subtitle={_t("applicantQuestionsAnsweredOnce")}
        onClose={onClose}
        closable={closable}
        _t={_t}
      />
      <div className="bsm-body">
        <button type="button" className="bsm-back" onClick={onBack}>
          <IoChevronBack />
          {_t("backToSteps")}
        </button>
        <p className="bsm-optional">
          {questions.required
            ? _t("applicantQuestionsRequiredNote")
            : _t("applicantQuestionsOptional")}
        </p>

        {questions.linkedin && (
          <>
            <div className="bsm-question">
              {_t("applicantQuestionLinkedin")}
            </div>
            <input
              className="bsm-input"
              type="url"
              placeholder="https://www.linkedin.com/in/..."
              value={linkedin}
              maxLength={300}
              aria-label={_t("applicantQuestionLinkedin")}
              onChange={(event) => setLinkedin(event.target.value)}
            />
          </>
        )}

        {questions.custom.map((question) => (
          <div key={question.id}>
            <div className="bsm-question">{question.text}</div>
            <ApplicantQuestionInput
              question={question}
              value={answers[question.id]}
              onChange={(value) =>
                setAnswers((current) => ({ ...current, [question.id]: value }))
              }
              _t={_t}
            />
          </div>
        ))}
      </div>
      <div className="bsm-footer bsm-footer-actions">
        <span>
          {sharesOnComplete &&
            `${_t("completingThisStepSharesWith")} ${landlordName}.`}
        </span>
        <button
          type="button"
          className="bsm-primary"
          disabled={saving || !canComplete}
          onClick={handleComplete}
        >
          {saving ? _t("saving") : _t("complete")}
        </button>
      </div>
    </>
  );
};

/**
 * Steps a landlord asks for before an application (or profile) is shared.
 * `steps`: [{ key, requiredBeforeSharing, done }] for the active requirements.
 * Completing the last open step shares. Closing shares too when every step
 * required before sharing is done; otherwise it cancels the share.
 */
const BeforeSharingModal = ({
  user,
  landlordName,
  steps,
  memberStatuses,
  applicationId,
  // Application used by the KYC-AML form (defaults to applicationId); null
  // makes the form use the landlord / workspace instead
  identificationApplicationId,
  applicationTitle,
  minAdults = 1,
  household,
  // { household_members, minors, dogs }: the household questions the
  // landlords ticked. null (not sent): all asked, as before the config.
  householdQuestions = null,
  // { workspace_id, questions, saved } for the applicant questions step
  applicantQuestions = null,
  workspace,
  landlordEmail,
  onStepDone,
  onClose,
  // "before": shared once the required steps are done.
  // "after": already shared; the landlord is waiting for the steps.
  mode = "before",
  // false when the step is mandatory (no close button)
  closable = true,
  // Signing as a guarantor of this tenant
  guarantorForTenantId = null,
}) => {
  const { _t } = useTranslator();
  const isAfterSharing = mode === "after";
  const [view, setView] = useState("steps");
  const [householdPets, setHouseholdPets] = useState(household ?? null);
  const [applicantData, setApplicantData] = useState(applicantQuestions);

  const eyebrow = isAfterSharing
    ? `${_t("tenantStepsSharedWith")} ${landlordName}`
    : `${_t("beforeSharingWith")} ${landlordName}`;
  const householdStep = steps.find(
    (step) => step.key === TENANT_REQUIREMENTS.HOUSEHOLD
  );
  const identificationStep = steps.find(
    (step) => step.key === TENANT_REQUIREMENTS.CLIENT_IDENTIFICATION
  );
  const applicantStep = steps.find(
    (step) => step.key === APPLICANT_QUESTIONS_STEP
  );
  const pendingRequired = steps.filter(
    (step) => step.requiredBeforeSharing && !step.done
  );
  // The share runs when the last open step is completed
  const sharesWhenDone = (key) =>
    steps.every((step) => step.key === key || step.done);
  const hasOpenSteps = steps.some((step) => !step.done);
  const requiredLabels = pendingRequired.map((step) =>
    step.key === TENANT_REQUIREMENTS.HOUSEHOLD
      ? _t("householdAndPetsLower")
      : step.key === APPLICANT_QUESTIONS_STEP
        ? _t("applicantQuestionsLower")
        : _t("theKycAmlForm")
  );

  // Members of the application and their completed guarantors, with their
  // KYC-AML status
  const currentUserId = user?._id?.toString();
  const currentUserEmail = user?.email?.toLowerCase();
  // From GET /applications/:id/identification-statuses: accepted members
  const members = (memberStatuses ?? []).map((member) => ({
    id: member.id?.toString(),
    isMe:
      member.id?.toString() === currentUserId ||
      member.email?.toLowerCase() === currentUserEmail,
    name: resolveParticipantName(member),
    signed: isSigned(member.client_identification_consent_status),
    guarantors: (member.guarantors ?? [])
      .filter(
        (guarantor) =>
          guarantor?.profile_completed === true &&
          guarantor?.email?.toLowerCase() !== currentUserEmail
      )
      .map((guarantor) => ({
        id: guarantor._id,
        name: resolveParticipantName(guarantor),
        signed: isSigned(guarantor.client_identification_consent_status),
      })),
  }));
  const myGuarantors = members.find((member) => member.isMe)?.guarantors ?? [];
  const otherMembers = members.filter((member) => !member.isMe);
  const someoneElseUnsigned =
    myGuarantors.some((guarantor) => !guarantor.signed) ||
    otherMembers.some(
      (member) =>
        !member.signed ||
        member.guarantors.some((guarantor) => !guarantor.signed)
    );

  // Rendered into <body> so a parent modal's styles (backdrop-filter,
  // overflow) never clip or trap it
  const inBody = (node) => createPortal(node, document.body);

  if (view === "household") {
    return inBody(
      <div className="bsm-overlay">
        <div className="bsm-modal" role="dialog" aria-modal="true">
          <HouseholdPetsStep
            eyebrow={eyebrow}
            applicationId={applicationId}
            applicationTitle={applicationTitle}
            landlordName={landlordName}
            minAdults={minAdults}
            questions={householdQuestions}
            initialData={householdPets?.household_data}
            sharesOnComplete={
              !isAfterSharing &&
              !householdStep?.done &&
              sharesWhenDone(TENANT_REQUIREMENTS.HOUSEHOLD)
            }
            onBack={() => setView("steps")}
            onClose={onClose}
            closable={closable}
            onCompleted={async (saved) => {
              if (saved) setHouseholdPets(saved);
              setView("steps");
              await onStepDone(TENANT_REQUIREMENTS.HOUSEHOLD);
            }}
            _t={_t}
          />
        </div>
      </div>
    );
  }

  if (view === "applicantQuestions" && applicantData) {
    return inBody(
      <div className="bsm-overlay">
        <div className="bsm-modal" role="dialog" aria-modal="true">
          <ApplicantQuestionsStep
            eyebrow={eyebrow}
            landlordName={landlordName}
            data={applicantData}
            sharesOnComplete={
              !isAfterSharing &&
              !applicantStep?.done &&
              sharesWhenDone(APPLICANT_QUESTIONS_STEP)
            }
            onBack={() => setView("steps")}
            onClose={onClose}
            closable={closable}
            onCompleted={async (saved) => {
              setApplicantData((current) => ({
                ...current,
                saved,
                complete: true,
              }));
              setView("steps");
              await onStepDone(APPLICANT_QUESTIONS_STEP);
            }}
            _t={_t}
          />
        </div>
      </div>
    );
  }

  if (view === "identification") {
    return inBody(
      <ClientIdentificationForm
        user={user}
        workspace={workspace}
        applicationId={
          identificationApplicationId === undefined
            ? (applicationId ?? null)
            : identificationApplicationId
        }
        landlordEmail={landlordEmail ?? null}
        guarantorForTenantId={guarantorForTenantId}
        onClose={() => setView("steps")}
        onSubmitted={async () => {
          setView("steps");
          await onStepDone(TENANT_REQUIREMENTS.CLIENT_IDENTIFICATION);
        }}
      />
    );
  }

  const renderStep = ({ done, title, description, openView }) => (
    <div className="bsm-step">
      <StatusIcon state={done ? "done" : "open"} />
      <div className="bsm-step-copy">
        <div className="bsm-step-title">{title}</div>
        <div className="bsm-step-description">{description}</div>
      </div>
      {done ? (
        <button
          type="button"
          className="bsm-edit"
          onClick={() => setView(openView)}
        >
          {_t("edit")}
        </button>
      ) : (
        <button
          type="button"
          className="bsm-primary"
          onClick={() => setView(openView)}
        >
          {_t("complete")}
        </button>
      )}
    </div>
  );

  // Guarantor rows use the application's guarantor indentation and badge
  const renderMemberRow = ({ id, name, signed, isGuarantor }) => (
    <div
      key={id}
      className={`bsm-step bsm-member${
        isGuarantor ? " application-guarantor-row" : ""
      }`}
    >
      <StatusIcon state={signed ? "done" : "waiting"} />
      <div className="bsm-step-copy">
        <div className="bsm-step-title bsm-member-title">
          <span>
            {name} · {_t("kycAmlForm")}
          </span>
          {isGuarantor && (
            <span className="app-member-badge app-member-badge-guarantor">
              {_t("guarantor")}
            </span>
          )}
        </div>
        <div className="bsm-step-description">
          {signed ? _t("kycSigned") : _t("kycPending")}
        </div>
      </div>
    </div>
  );

  const renderGuarantors = (guarantors) =>
    guarantors.map((guarantor) =>
      renderMemberRow({ ...guarantor, isGuarantor: true })
    );

  let footer = null;
  if (isAfterSharing) {
    if (
      identificationStep &&
      (!identificationStep.done || someoneElseUnsigned)
    ) {
      footer = `${_t("untilEveryoneSigns")} ${landlordName} ${_t("seesTheApplicationButNoReport")}`;
    }
  } else if (requiredLabels.length > 0) {
    footer = `${landlordName} ${_t("seesTheApplicationAsSoonAs")} ${requiredLabels.join(" & ")} ${_t("isCompleted")}`;
  } else if (
    identificationStep &&
    (!identificationStep.done || someoneElseUnsigned)
  ) {
    footer = `${_t("untilEveryoneSigns")} ${landlordName} ${_t("seesTheApplicationButNoReport")}`;
  } else if (hasOpenSteps) {
    footer = `${_t("closeThisWindowToShareWith")} ${landlordName}. ${_t("youCanCompleteTheRestLater")}`;
  }

  return inBody(
    <div className="bsm-overlay">
      <div className="bsm-modal" role="dialog" aria-modal="true">
        <Header
          eyebrow={eyebrow}
          title={
            isAfterSharing
              ? `${landlordName} ${_t("needsYourSignatureToReview")}`
              : `${landlordName} ${
                  steps.length > 1
                    ? _t("asksForTwoThingsFirst")
                    : _t("asksForOneThingFirst")
                }`
          }
          subtitle={
            isAfterSharing
              ? `${
                  applicationId
                    ? `${applicationTitle ?? ""} ${_t("hasReached")}`
                    : _t("yourProfileHasReached")
                } ${landlordName}. ${
                  guarantorForTenantId
                    ? _t("guarantorAlsoSignsIdentificationForm")
                    : _t("eachTenantSignsIdentificationForm")
                }`
              : householdStep && identificationStep
                ? _t("beforeSharingBothDescription")
                : householdStep
                  ? _t("beforeSharingHouseholdDescription")
                  : identificationStep
                    ? _t("beforeSharingIdentificationDescription")
                    : _t("beforeSharingApplicantQuestionsDescription")
          }
          onClose={onClose}
          closable={closable}
          _t={_t}
        />
        <div className="bsm-body">
          {householdStep && (
            <div className="bsm-section">
              <div className="bsm-section-title">
                {_t("forTheApplicationOnce")}
              </div>
              {renderStep({
                done: householdStep.done,
                title: _t("householdAndPets"),
                description:
                  householdStep.done && householdPets?.household_data
                    ? householdSummary(householdPets.household_data, _t)
                    : applicationId
                      ? _t("householdStepDescription")
                      : _t("householdStepDescriptionProfile"),
                openView: "household",
              })}
            </div>
          )}
          {applicantStep && applicantData && (
            <div className="bsm-section">
              <div className="bsm-section-title">
                {applicantStep.requiredBeforeSharing
                  ? _t("applicantQuestionsRequiredNote")
                  : _t("applicantQuestionsOptional")}
              </div>
              {renderStep({
                done: applicantStep.done,
                title: _t("applicantQuestions"),
                description:
                  applicantStep.done && applicantData.saved
                    ? [
                        applicantData.saved.linkedin ? "LinkedIn" : null,
                        ...(applicantData.saved.answers ?? []).map(
                          (answer) =>
                            `${answer.text}: ${answerText(answer.value, _t)}`
                        ),
                      ]
                        .filter(Boolean)
                        .join(" · ") || _t("applicantQuestionsStepDescription")
                    : _t("applicantQuestionsStepDescription"),
                openView: "applicantQuestions",
              })}
            </div>
          )}
          {identificationStep && (
            <div className="bsm-section">
              <div className="bsm-section-title">
                {_t("forEachTenantSignsOwn")}
              </div>
              <div className="bsm-member-list">
                {renderStep({
                  done: identificationStep.done,
                  title: `${_t("kycYou")} · ${_t("kycAmlIdentificationForm")}`,
                  description: identificationStep.done
                    ? _t("kycSigned")
                    : isAfterSharing || identificationStep.requiredBeforeSharing
                      ? _t("kycStepDescription")
                      : `${_t("kycStepDescription")} ${_t("kycCanCompleteAfterSharing")}`,
                  openView: "identification",
                })}
                {renderGuarantors(myGuarantors)}
                {otherMembers.map((member) => [
                  renderMemberRow(member),
                  ...renderGuarantors(member.guarantors),
                ])}
              </div>
            </div>
          )}
        </div>
        {footer && <div className="bsm-footer">{footer}</div>}
      </div>
    </div>
  );
};

export default BeforeSharingModal;
